import Script from 'next/script'
import React from 'react'
import { isDesktop } from '../../client'
import { loadScriptOnce } from '../../utils'
import { isShortCode } from '../Parser/Parser'
import { ZoomIn } from '../ZoomIn'
import {
  SpecialBlockquote,
  StyledBlockquote,
  StyledFigure,
  StyledFigureWrapper,
  StyledH3,
  StyledMoreArticles,
  StyledOL,
  StyledP,
  StyledUL,
} from './ContentParser.style'

import {
  cleanOutEndingEmptyLines,
  embedRegex,
  getWidthAndHeightFromEmbedTag,
} from './utils'
import { PWithADWrap } from './PWithADWrap'
import { IParseHTML } from './types'
import dynamic from 'next/dynamic'

const ContentIframe = dynamic(() => import('./ContentIframe'), { ssr: false })
const SocialEmbed = dynamic(() => import('./SocialEmbed'))

export const parseHTML = ({
  html,
  adMidArticle = true,
  keyPrefix = '',
  addArticleBox = true,
  isArticleVod,
  isArticle,
  isFaceLift = false,
}: IParseHTML) => {
  if (!html) {
    return []
  }
  const counts = { p: 0, char: 0, ad: 0, word: 0 }
  let figureCounter = 2

  let foundFirstImage = false,
    hasMoreArticles = false,
    moreArticlesHead: {} | null | undefined = null

  const childNodes = cleanOutEndingEmptyLines(html)

  const nodesCount = childNodes.length - 1
  return childNodes
    .map((t, index) => {
      const shortcodeElement = isShortCode(
        t.innerHTML,
        keyPrefix + '_cp_el_' + index
      )
      if (shortcodeElement) {
        return shortcodeElement
      }

      const key = keyPrefix + '_cp_el_' + index

      const script = t.querySelector('script')
      const iframe = t.querySelector('iframe')
      switch (t.nodeName.toLowerCase()) {
        case 'p':
          hasMoreArticles = false
          if (t.innerHTML.match(embedRegex)) {
            const urlMatch = t.innerHTML.match(embedRegex)
            if (urlMatch) {
              const url = urlMatch[1]
              const dimension = getWidthAndHeightFromEmbedTag(t.innerHTML)
              return <SocialEmbed url={url} dimension={dimension} />
            }
          }
          if (script) {
            try {
              if (script.src) {
                const url = new URL(script.src)
                url && loadScriptOnce({ src: url.href, id: url.href })
                return null
              } else {
                return (
                  <Script
                    dangerouslySetInnerHTML={{
                      __html: script.innerText,
                    }}
                    key={Date.now()}
                  />
                )
              }
            } catch (e) {
              console.log(
                '%cContentParser ',
                'background-color:darkred;color:white;padding:2px 5px;',
                e
              )
            }
          }
          if (iframe) {
            return <ContentIframe iframeEl={iframe} key={key} />
          }
          if (
            [' ', '&nbsp;', ''].includes(t.innerText.toLowerCase()) ||
            t.innerHTML === '&nbsp;'
          ) {
            return null
          }

          // debugger
          if (t.innerText.length > 0) {
            counts.p++
          }
          counts.char += t.innerText.length
          counts.word += t.innerText.split(' ').length

          if (addArticleBox && t.innerText.length > 0 && nodesCount > index) {
            switch (counts.ad) {
              case 0:
                if (counts.p >= 1 && counts.word >= 60) {
                  counts.ad++
                  counts.p = 0
                  counts.char = 0
                  counts.word = 0
                  return (
                    <PWithADWrap
                      key={key}
                      for_key={key}
                      html={t.innerHTML}
                      cls={t.className}
                      adId={`In_article_box_${counts.ad}`}
                      isArticleVod={!!isArticleVod}
                      addType={'taboola'}
                    />
                  )
                }
                break
              case 1:
              case 2:
              case 3:
                if (counts.p >= 2 && counts.word >= 120) {
                  counts.ad++
                  counts.p = 0
                  counts.char = 0
                  counts.word = 0
                  return (
                    <PWithADWrap
                      key={key}
                      for_key={key}
                      html={t.innerHTML}
                      cls={t.className}
                      adId={'In_article_box_' + counts.ad}
                      isArticleVod={!!isArticleVod}
                    />
                  )
                }
                break
              case 4:
                if (!isDesktop() && counts.p >= 2 && counts.word >= 120) {
                  counts.ad++
                  counts.p = 0
                  counts.char = 0
                  counts.word = 0
                  return (
                    <PWithADWrap
                      key={key}
                      for_key={key}
                      html={t.innerHTML}
                      cls={t.className}
                      adId={'In_article_box_' + counts.ad}
                      isArticleVod={!!isArticleVod}
                    />
                  )
                }
            }
          }
          return (
            <StyledP
              key={key}
              dangerouslySetInnerHTML={{ __html: t.innerHTML }}
              className={t.className}
              theme={{ isFaceLift }}
            />
          )
        case 'figure': {
          hasMoreArticles = false
          const key = t.querySelector('img')?.src || Date.now()
          const html = t.innerHTML.replace(
            /\/image\/upload\//g,
            '/image/upload/t_image_article_800/'
          )
          // if (!foundFirstImage && adMidArticle && index < nodesCount) {
          //   foundFirstImage = true
          //
          //   return (
          //       <div key={key} style={{ position: 'relative' }}>
          //         <StyledFigure
          //             dangerouslySetInnerHTML={{ __html: html }}
          //             className={`${t.className} article-image`}
          //             // TODO: Move style to styles file somehow
          //             style={{ marginBottom: '10px' }}
          //         />
          //         {isArticle && <ZoomIn index={1} key={key} />}
          //         {/*{midArticleTaboola}*/}
          //       </div>
          //   )
          // }
          return (
            <StyledFigureWrapper key={key}>
              <StyledFigure
                dangerouslySetInnerHTML={{ __html: html }}
                className={`${t.className} article-image`}
              />
              {isArticle && <ZoomIn index={figureCounter++} key={key} />}
            </StyledFigureWrapper>
          )
        }
        case 'blockquote':
          hasMoreArticles = false
          if (
            t.className.includes('instagram-media') ||
            t.className.includes('tiktok-embed') ||
            t.className.includes('reddit-embed-bq') ||
            t.className.includes('facebook') ||
            t.className.includes('twitter-tweet')
          ) {
            return (
              <SpecialBlockquote
                key={key}
                dangerouslySetInnerHTML={{ __html: t.innerHTML }}
                className={t.className}
              />
            )
          }
          return (
            <StyledBlockquote
              key={key}
              dangerouslySetInnerHTML={{ __html: t.innerHTML }}
              className={t.className}
            />
          )
        case 'h3':
          moreArticlesHead = (
            <StyledH3
              dangerouslySetInnerHTML={{ __html: t.innerHTML }}
              key={key}
            />
          )
          hasMoreArticles = false
          if (t.innerText.includes('כתבות נוספות')) {
            hasMoreArticles = true
            return null
          }
          return moreArticlesHead
        case 'ul':
          if (hasMoreArticles) {
            hasMoreArticles = false
            if (!foundFirstImage && adMidArticle && index < nodesCount) {
              foundFirstImage = true
              return (
                <React.Fragment key={key}>
                  <StyledMoreArticles theme={{ isFaceLift }}>
                    {moreArticlesHead}
                    <StyledUL
                      dangerouslySetInnerHTML={{ __html: t.innerHTML }}
                    />
                  </StyledMoreArticles>
                  {/*{midArticleTaboola}*/}
                </React.Fragment>
              )
            }
            return (
              <StyledMoreArticles key={key}>
                {moreArticlesHead}
                <StyledUL dangerouslySetInnerHTML={{ __html: t.innerHTML }} />
              </StyledMoreArticles>
            )
          }
          moreArticlesHead = null
          hasMoreArticles = false
          return (
            <StyledUL
              dangerouslySetInnerHTML={{ __html: t.innerHTML }}
              key={key}
            />
          )
        case 'ol':
          return (
            <StyledOL
              key={key}
              dangerouslySetInnerHTML={{ __html: t.innerHTML }}
            />
          )
        default:
          return (
            <div key={key} dangerouslySetInnerHTML={{ __html: t.outerHTML }} />
          )
      }
    })
    .filter(el => el !== null) as JSX.Element[]
}
